import type { SxProps, Theme } from "@mui/material/styles";
import { useFormContext } from "react-hook-form";

import { Box } from "ui/box";
import { Checkbox } from "ui/checkbox";
import { Chip } from "ui/chip";
import { Select, type OptionType } from "ui/select";

import { useCreativeTemplateOptions } from "hooks/api/use-creative-template-options";

import { ChannelIcon } from "components/partials/channel-icon/channel-icon";
import {
  getDeliverableLabel,
  type CampaignWriterDeliverable,
} from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.helper";
import type { CreateCampaignWithWriterFormData } from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.types";

import { toSpacedTitleCase } from "utilities";
import { getTouchpointClassChipColor } from "utilities/touchpoint-class-chip";

import Icons from "assets";

const POPULATION_LABELS: Record<CampaignWriterDeliverable["populationType"], string> = {
  student: "Student",
  parent: "Parent",
};

export const TEMPLATE_COLUMN_SX: SxProps<Theme> = { flex: 1, minWidth: 0 };

interface DeliverableItemProps {
  deliverable: CampaignWriterDeliverable;
}

const DeliverableItem = ({ deliverable }: DeliverableItemProps) => {
  const { watch, setValue } = useFormContext<CreateCampaignWithWriterFormData>();
  const deliverableName = getDeliverableLabel(deliverable);
  const selectedDeliverables = watch("deliverables") ?? [];
  const deliverableTemplates = watch("deliverableTemplates") ?? {};
  const isSelected = selectedDeliverables.includes(deliverable.id);
  const { templateOptions, isLoading: isLoadingTemplates } = useCreativeTemplateOptions(deliverable.channel);

  const handleChange = (checked: boolean) => {
    const nextDeliverables = checked
      ? [...selectedDeliverables, deliverable.id]
      : selectedDeliverables.filter((id) => id !== deliverable.id);

    setValue("deliverables", nextDeliverables);
  };

  const handleTemplateChange = (selected: OptionType | null) => {
    setValue("deliverableTemplates", {
      ...deliverableTemplates,
      [deliverable.id]: (selected?.value as string) ?? "",
    });
  };

  const selectedTemplateId = deliverableTemplates[deliverable.id];

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1, pr: 1.5 }}>
      <div className="flex flex-1 items-center gap-2 min-w-0">
        <Checkbox
          size="sm"
          checked={isSelected}
          onChange={handleChange}
          name={`deliverable-${deliverable.id}`}
          label={
            <span className="flex items-center gap-2 text-gray-900">
              <span className="flex items-center gap-2">
                <ChannelIcon channel={deliverable.channel} />
                {deliverableName}
                <span className="text-gray-500">{POPULATION_LABELS[deliverable.populationType]}</span>
              </span>
              {deliverable.classes.length > 0 && (
                <span className="flex items-center gap-2">
                  {deliverable.classes.map((classType) => {
                    const normalizedClassType = classType.toUpperCase();

                    return (
                      <Chip
                        key={`${deliverable.id}-${classType}`}
                        size="small"
                        variant="outlined"
                        color={getTouchpointClassChipColor(normalizedClassType)}
                        label={toSpacedTitleCase(normalizedClassType)}
                      />
                    );
                  })}
                </span>
              )}
            </span>
          }
        />
      </div>
      <Box sx={TEMPLATE_COLUMN_SX}>
        <Select
          selectName={`deliverable-template-${deliverable.id}`}
          ariaLabel={`Select creative template for ${deliverableName}`}
          size="small"
          disabled={!isSelected || isLoadingTemplates}
          loading={isLoadingTemplates}
          options={templateOptions}
          value={templateOptions.find((option) => option.value === selectedTemplateId) ?? null}
          onChange={(selected) => handleTemplateChange(selected)}
          placeholder="Select template"
          startIcon={<Icons.Search className="text-aqua-600" />}
        />
      </Box>
    </Box>
  );
};

export default DeliverableItem;
